import { useMemo, useRef, useSyncExternalStore } from "react"

export type TargetLivePosition = {
  lat: number
  lng: number
  absoluteHeight?: number
}

const positions = new Map<string, TargetLivePosition>()
const perTargetListeners = new Map<string, Set<() => void>>()

const emitForTarget = (targetId: string) => {
  perTargetListeners.get(targetId)?.forEach((l) => l())
}

const sameLivePosition = (
  left: TargetLivePosition | undefined,
  right: TargetLivePosition,
): boolean =>
  left?.lat === right.lat &&
  left?.lng === right.lng &&
  left?.absoluteHeight === right.absoluteHeight

export const setTargetLivePosition = (
  targetId: string,
  position: TargetLivePosition,
): void => {
  const prev = positions.get(targetId)
  const next: TargetLivePosition = {
    lat: position.lat,
    lng: position.lng,
    ...(typeof position.absoluteHeight === "number"
      ? { absoluteHeight: position.absoluteHeight }
      : prev?.absoluteHeight !== undefined
        ? { absoluteHeight: prev.absoluteHeight }
        : {}),
  }

  if (sameLivePosition(prev, next)) {
    return
  }

  positions.set(targetId, next)
  emitForTarget(targetId)
}

export const removeTargetLivePosition = (targetId: string): void => {
  if (!positions.has(targetId)) return
  positions.delete(targetId)
  emitForTarget(targetId)
  perTargetListeners.delete(targetId)
}

export const getTargetLivePosition = (
  targetId: string | null | undefined,
): TargetLivePosition | null =>
  (targetId ? positions.get(targetId) : undefined) ?? null

const subscribeToTargetPosition = (
  targetId: string,
  listener: () => void,
): (() => void) => {
  let set = perTargetListeners.get(targetId)
  if (!set) {
    set = new Set()
    perTargetListeners.set(targetId, set)
  }
  set.add(listener)
  return () => {
    const s = perTargetListeners.get(targetId)
    if (!s) return
    s.delete(listener)
    if (s.size === 0) perTargetListeners.delete(targetId)
  }
}

/**
 * Subscribe to live position updates for a single target (per-ID subscription).
 * Only the component for the moved target re-renders — other markers are unaffected.
 * Priority on the map: drag preview > live position > target.lat/lng from React Query.
 */
export const useTargetLivePosition = (
  targetId: string | null | undefined,
): TargetLivePosition | null =>
  useSyncExternalStore(
    (listener) =>
      targetId ? subscribeToTargetPosition(targetId, listener) : () => {},
    () => getTargetLivePosition(targetId),
    () => null,
  )

const buildPositionsSnapshotKey = (ids: readonly string[]): string =>
  ids
    .map((id) => {
      const position = positions.get(id)
      if (!position) {
        return `${id}:missing`
      }

      return `${id}:${position.lat}:${position.lng}:${position.absoluteHeight ?? ""}`
    })
    .join("|")

export const useTargetLivePositionsMap = (
  targetIds: readonly string[],
): ReadonlyMap<string, TargetLivePosition> => {
  const stableIds = useMemo(
    () => [...new Set(targetIds.filter(Boolean))].sort(),
    [targetIds],
  )
  const snapshotRef = useRef<{
    key: string
    map: ReadonlyMap<string, TargetLivePosition>
  }>({
    key: "",
    map: new Map(),
  })

  return useSyncExternalStore(
    (listener) => {
      if (stableIds.length === 0) {
        return () => {}
      }

      const unsubs = stableIds.map((id) =>
        subscribeToTargetPosition(id, listener),
      )
      return () => {
        unsubs.forEach((unsub) => unsub())
      }
    },
    () => {
      const nextKey = buildPositionsSnapshotKey(stableIds)
      if (snapshotRef.current.key === nextKey) {
        return snapshotRef.current.map
      }

      const map = new Map<string, TargetLivePosition>()
      stableIds.forEach((id) => {
        const position = positions.get(id)
        if (position) {
          map.set(id, position)
        }
      })
      snapshotRef.current = { key: nextKey, map }
      return map
    },
    () => new Map(),
  )
}
